<template>
    <v-dialog
        v-model="isOpen"
        fullscreen
        persistent
        :retain-focus="false"
        transition="dialog-bottom-transition"
    >
        <v-card class="d-flex flex-column" height="100%">
            <!-- Compact Header -->
            <v-toolbar flat color="error" dark dense>
                <v-toolbar-title>Problem</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon @click="closeModal">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <!-- Content -->
            <v-card-text class="flex-grow-1 overflow-y-auto pa-4 pb-0">
                <!-- Simple Problem Report Form -->
                <v-select
                    v-model="problemType"
                    :items="problemTypes"
                    label="Was ist das Problem?"
                    outlined
                    dense
                    hide-details="auto"
                    color="error"
                    class="mb-3"
                ></v-select>

                <v-textarea
                    v-model="problemText"
                    label="Beschreibung..."
                    outlined
                    dense
                    auto-grow
                    rows="4"
                    color="error"
                    counter="500"
                    maxlength="500"
                    hide-details="auto"
                    class="mb-4"
                />

                <!-- Engraving Error Booking Section (only if enabled) -->
                <template v-if="showEngravingSection">
                    <v-divider class="my-6"></v-divider>

                    <h3 class="text-subtitle-1 font-weight-bold mb-4">
                        Fehlproduktion ausbuchen
                    </h3>

                    <div v-if="loadingFullOrder" class="text-center py-8">
                        <v-progress-circular
                            indeterminate
                            color="error"
                            size="48"
                        ></v-progress-circular>
                        <p class="mt-3 text-body-2">Bestelldaten werden geladen...</p>
                    </div>

                    <template v-else-if="fullOrder && fullOrder.billbeeOrder && fullOrder.billbeeOrder.Data && fullOrder.billbeeOrder.Data.OrderItems && fullOrder.billbeeOrder.Data.OrderItems.length > 0">
                        <p class="text-body-2 mb-3">
                            Wählen Sie die Produkte mit Fehlern aus
                            <v-chip
                                v-if="selectedEngravingItems.length > 0"
                                small
                                color="error"
                                dark
                                class="ml-2"
                            >
                                {{ selectedEngravingItems.length }} ausgewählt
                            </v-chip>
                        </p>

                        <v-row>
                            <v-col
                                v-for="item in fullOrder.billbeeOrder.Data.OrderItems"
                                :key="item.BillbeeId"
                                cols="12"
                                sm="6"
                            >
                                <v-card
                                    @click="$event => toggleEngravingProduct(item, $event)"
                                    outlined
                                    :class="{'error--text': isEngravingProductSelected(item)}"
                                    class="pa-3"
                                >
                                    <div class="d-flex align-center justify-space-between mb-2">
                                        <div class="flex-grow-1">
                                            <div class="text-caption grey--text">{{ item.Product.SKU }}</div>
                                            <div class="text-body-2 font-weight-bold">{{ item.Product.Title }}</div>
                                            <div class="text-caption">Menge: {{ item.Quantity }}</div>
                                        </div>
                                        <v-checkbox
                                            :input-value="isEngravingProductSelected(item)"
                                            color="error"
                                            readonly
                                            dense
                                            hide-details
                                            class="ma-0"
                                        ></v-checkbox>
                                    </div>

                                    <v-expand-transition>
                                        <div v-if="isEngravingProductSelected(item)" class="mt-3">
                                            <v-select
                                                :value="getItemErrorReason(item).type"
                                                @input="val => setItemErrorReason(item, val, getItemErrorReason(item).details)"
                                                :items="errorTypes"
                                                label="Fehlertyp"
                                                outlined
                                                dense
                                                hide-details="auto"
                                                color="error"
                                                class="mb-2"
                                                @click.stop
                                                @mousedown.stop
                                            ></v-select>
                                            <v-text-field
                                                v-if="getItemErrorReason(item).type === 'Eigener Grund'"
                                                :value="getItemErrorReason(item).details"
                                                @input="val => setItemErrorReason(item, getItemErrorReason(item).type, val)"
                                                label="Beschreibung"
                                                outlined
                                                dense
                                                hide-details="auto"
                                                color="error"
                                                @click.stop
                                                @mousedown.stop
                                            ></v-text-field>
                                        </div>
                                    </v-expand-transition>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-btn
                            @click="bookEngravingErrorAction"
                            color="error"
                            :disabled="!canReportEngravingProblem"
                            :loading="submitting"
                            block
                            large
                            class="mt-4"
                        >
                            <v-icon left>mdi-package-down</v-icon>
                            Ausbuchen ({{ selectedEngravingItems.length }})
                        </v-btn>
                    </template>

                    <v-alert v-else type="warning" outlined dense>
                        Keine Produkte in dieser Bestellung gefunden.
                    </v-alert>
                </template>
            </v-card-text>

            <!-- Fixed Bottom Button -->
            <v-card-actions class="pa-4 elevation-4">
                <v-btn
                    @click="reportProblem"
                    color="error"
                    :disabled="!canReportGeneralProblem"
                    :loading="submitting"
                    block
                    large
                >
                    <v-icon left>mdi-send</v-icon>
                    Problem melden
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import ProductionService from '@/services/ProductionService';

export default {
    name: 'ProblemModal',
    props: {
        showEngravingSection: {
            type: Boolean,
            default: false
        },
        currentItem: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            default: null
        },
        currentPacklistItemUserLaser: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            isOpen: true,
            submitting: false,
            problemType: null,
            problemText: null,
            problemTypes: [
                'Fehlende Produkte',
                'Beschädigte Produkte',
                'Falsche Produkte',
                'Druckfehler',
                'Systemfehler',
                'Sonstiges'
            ],
            errorTypes: [
                'Eigener Grund',
                'Kratzer',
                'Blasen',
                'Verfärbung',
                'Falsche Gravur',
                'Unvollständige Gravur',
                'Material defekt'
            ],
            loadingFullOrder: false,
            fullOrder: null,
            selectedEngravingItems: [],
            errorReasons: {}
        }
    },
    computed: {
        canReportGeneralProblem() {
            return this.problemType && this.problemText && this.problemText.trim().length > 0;
        },
        canReportEngravingProblem() {
            if (!this.showEngravingSection) return false;
            return this.selectedEngravingItems.length > 0 &&
                   this.selectedEngravingItems.every(item => this.hasItemErrorReason(item));
        }
    },
    methods: {
        closeModal() {
            this.isOpen = false;
        },
        async reportProblem() {
            if (!this.canReportGeneralProblem) return;

            this.submitting = true;
            try {
                let note = this.problemType + ': ' + this.problemText;
                this.$emit('reportProblem', note);

                setTimeout(() => {
                    this.submitting = false;
                    this.closeModal();
                }, 1000);
            } catch (error) {
                this.submitting = false;
                console.error('Error reporting problem:', error);
            }
        },
        async bookEngravingErrorAction() {
            if (!this.canReportEngravingProblem) return;

            this.submitting = true;
            try {
                const damagedItems = this.selectedEngravingItems.map(item => {
                    const reason = this.getItemErrorReason(item);
                    let reasonText = reason.type;
                    if (reason.type === 'Eigener Grund' && reason.details) {
                        reasonText += ': ' + reason.details;
                    }

                    return {
                        billbeeId: item.BillbeeId,
                        sku: item.Product.SKU,
                        title: item.Product.Title,
                        quantity: item.Quantity,
                        reason: reasonText
                    };
                });

                await ProductionService.createDamagedItems({
                    orderId: this.currentItem.orderId,
                    items: damagedItems,
                    userId: this.user ? this.user.id : null,
                    userLaser: this.currentPacklistItemUserLaser
                });

                this.$emit('bookEngravingError', damagedItems);

                setTimeout(() => {
                    this.submitting = false;
                    this.closeModal();
                }, 1000);
            } catch (error) {
                this.submitting = false;
                console.error('Error booking engraving error:', error);
                alert('Fehler beim Ausbuchen: ' + error.message);
            }
        },
        async getFullOrderInformation() {
            if (!this.currentItem || !this.currentItem.orderId) return;

            this.loadingFullOrder = true;
            try {
                const response = await this.$http.get(`/api/order/${this.currentItem.orderId}`);
                this.fullOrder = response.data;
            } catch (error) {
                console.error('Error loading full order:', error);
            } finally {
                this.loadingFullOrder = false;
            }
        },
        toggleEngravingProduct(item, event) {
            if (event && event.target && (event.target.tagName === 'INPUT' || event.target.closest('.v-input'))) {
                return;
            }

            const index = this.selectedEngravingItems.findIndex(i => i.BillbeeId === item.BillbeeId);
            if (index > -1) {
                this.selectedEngravingItems.splice(index, 1);
                this.$delete(this.errorReasons, item.BillbeeId);
            } else {
                this.selectedEngravingItems.push(item);
                this.$set(this.errorReasons, item.BillbeeId, { type: null, details: '' });
            }
        },
        isEngravingProductSelected(item) {
            return this.selectedEngravingItems.some(i => i.BillbeeId === item.BillbeeId);
        },
        setItemErrorReason(item, type, details) {
            this.$set(this.errorReasons, item.BillbeeId, { type, details: details || '' });
        },
        getItemErrorReason(item) {
            return this.errorReasons[item.BillbeeId] || { type: null, details: '' };
        },
        hasItemErrorReason(item) {
            const reason = this.getItemErrorReason(item);
            if (!reason.type) return false;
            if (reason.type === 'Eigener Grund') {
                return reason.details && reason.details.trim().length > 0;
            }
            return true;
        }
    },
    watch: {
        isOpen(newVal) {
            if (newVal) {
                // Reset form
                this.problemType = null;
                this.problemText = null;
                this.selectedEngravingItems = [];
                this.errorReasons = {};

                if (this.showEngravingSection) {
                    this.getFullOrderInformation();
                }
            } else {
                this.$emit('close-modal');
            }
        }
    }
}
</script>

<style scoped>
/* Minimal styles */
.v-toolbar {
    flex: 0 0 auto !important;
}

.v-card-actions {
    flex: 0 0 auto !important;
    background-color: white;
    position: sticky;
    bottom: 0;
    z-index: 10;
}
</style>
